import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bug, CheckSquare, Gavel, Lightbulb, RefreshCw } from 'lucide-react';
import { Badge } from '../ui/badge';
import ThinkingRoulette from '../ui/thinking-roulette';
import { entityPath } from '../../lib/productInsights';
import { analyzeMeeting, recoverMeeting, routeMeetingItem } from '../../services/meetings';

const time = seconds => Number.isFinite(seconds) ? `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}` : '';
const GROUPS = [
  { kind: 'action_item', title: 'Action items', icon: CheckSquare, add: () => 'Add to Inbox' },
  { kind: 'signal', title: 'Signals and evidence', icon: Lightbulb, add: item => item.problem_id ? 'Add as evidence' : 'Open as new problem' },
  { kind: 'bug', title: 'Bugs', icon: Bug, add: () => 'Send to Firefighter' },
  { kind: 'decision', title: 'Decisions', icon: Gavel, add: () => 'Record decision' },
];

// Where a routed item now lives, as a link the PM can follow.
function Destination({ item }) {
  const find = type => item.routed.find(r => r.type === type);
  if (item.kind === 'action_item') return <Link to="/inbox">In Inbox</Link>;
  if (item.kind === 'bug') return <Link to="/issues">In Firefighter</Link>;
  if (item.kind === 'decision') return <Link to={entityPath('decisions', find('decisions')?.id)}>In Decision Journal</Link>;
  const problem = find('problem_cards')?.id || item.problem_id;
  return <Link to={entityPath('problems', problem)}>{item.new_problem ? 'New problem in Discovery' : `Evidence on “${item.problem_title || 'problem'}”`}</Link>;
}

function AnalysisStatus({ job, analysis, local, busy, perform }) {
  const status = job.analysis_status;
  const analyze = label => <button className="v2-button secondary" disabled={busy} onClick={() => perform(() => analyzeMeeting(job.id))}><RefreshCw size={14}/>{label}</button>;
  if (job.status !== 'complete') return <p className="v2-muted">The call is analyzed automatically when the transcript is ready.</p>;
  if (status === 'analyzing' || status === 'queued') return <div className="meeting-analysis-status" role="status">
    {status === 'analyzing' ? <ThinkingRoulette label="Analyzing the call"/> : <strong>Analysis queued</strong>}
    <span className="v2-muted">{job.analysis_windows > 1 ? `Part ${Math.min((job.analysis_done || 0) + 1, job.analysis_windows)} of ${job.analysis_windows}. ` : ''}
      {local ? 'Runs on local Ollama. Keep the SPCC development app open.' : 'Starts within a minute.'}</span>
  </div>;
  if (status === 'needs_model') return <p className="meeting-analysis-status"><span><Link to="/autopilot">Choose an AI model in Autopilot</Link> to analyze this call.</span></p>;
  if (status === 'failed') return <div className="meeting-analysis-status"><p className="v2-error">{job.analysis_error || 'The call analysis failed.'}</p>{analyze('Try again')}</div>;
  if (!analysis) return <div className="meeting-analysis-status"><span className="v2-muted">This call was recorded before automatic analysis.</span>{analyze('Analyze call')}</div>;
  return <div className="meeting-analysis-status"><span className="v2-muted">Analyzed {new Date(analysis.analyzed_at).toLocaleString()} · {analysis.model?.replace(/^[a-z]+::/, '')}</span>{analyze('Analyze again')}</div>;
}

function Item({ item, group, job, busy, perform, jump }) {
  const routed = item.routed?.length > 0;
  const meta = [item.owner && `Owner: ${item.owner}`, item.due && `Due: ${item.due}`, item.kind === 'signal' && !routed && (item.problem_id ? `About “${item.problem_title || 'an open problem'}”` : 'Not covered by an open problem')].filter(Boolean);
  return <li className="meeting-item">
    <div className="meeting-item-head">
      <strong>{item.title}</strong>
      {item.severity && <Badge variant={['critical', 'high'].includes(item.severity) ? 'danger' : 'warning'}>{item.severity}</Badge>}
      {item.confidence === 'low' && <Badge variant="outline">low confidence</Badge>}
    </div>
    {item.detail && <p>{item.detail}</p>}
    {!!meta.length && <small className="v2-muted">{meta.join(' · ')}</small>}
    {item.quote ? <blockquote>“{item.quote}”{item.passage !== null && <button type="button" className="v2-text-action" onClick={() => jump(item.passage)}>{[time(item.at), item.speaker].filter(Boolean).join(' · ') || 'In transcript'}</button>}</blockquote>
      : <small className="v2-muted">The quote could not be found in the transcript, so this was not added automatically.</small>}
    <div className="v2-actions">{routed ? <Destination item={item}/>
      : <button className="v2-button secondary" disabled={busy} onClick={() => perform(() => routeMeetingItem(job.id, item.id))}>{group.add(item)}</button>}</div>
  </li>;
}

export default function MeetingDetail({ detail, local, busy, perform }) {
  const { job, chunks, analysis } = detail;
  const [botId, setBotId] = useState(''), [query, setQuery] = useState(''), [focus, setFocus] = useState(null);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? chunks.filter(c => c.content.toLowerCase().includes(q)) : chunks;
  }, [chunks, query]);
  const jump = index => {
    setQuery(''); setFocus(index);
    requestAnimationFrame(() => document.getElementById(`passage-${index}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
  };
  const items = analysis?.items || [];
  return <section className="v2-panel meeting-detail" aria-label="Meeting details">
    <h2>{job.title}</h2>
    {['creating', 'creation_unknown'].includes(job.status) && <form onSubmit={e => { e.preventDefault(); perform(() => recoverMeeting(job.id, botId)); }}>
      <p>Copy this meeting’s bot ID from your Recall dashboard to recover an unconfirmed request.</p><label className="v2-field"><span>Recall bot ID</span><input required value={botId} onChange={e => setBotId(e.target.value)}/></label><button className="v2-button secondary" disabled={busy}>Recover bot</button></form>}
    <div className="meeting-analysis" aria-label="Call analysis">
      <h3>Call analysis</h3>
      <AnalysisStatus job={job} analysis={analysis} local={local} busy={busy} perform={perform}/>
      {analysis?.summary && <p className="meeting-summary">{analysis.summary}</p>}
      {analysis?.truncated && <p className="v2-muted">This call is very long. The analysis covers its first part only; the full transcript is below.</p>}
      {analysis && !items.length && <p className="v2-muted">No action items, product signals, bugs, or decisions were found in this call.</p>}
      {GROUPS.map(group => {
        const rows = items.filter(i => i.kind === group.kind);
        return rows.length > 0 && <section key={group.kind} className="meeting-group">
          <h4><group.icon size={16} aria-hidden="true"/>{group.title} <span className="v2-tag">{rows.length}</span></h4>
          <ul className="meeting-items">{rows.map(item => <Item key={item.id} item={item} group={group} job={job} busy={busy} perform={perform} jump={jump}/>)}</ul>
        </section>;
      })}
    </div>
    <div className="meeting-transcript-section">
      <div className="meeting-transcript-head">
        <h3>Transcript {chunks.length > 0 && <span className="v2-tag">{chunks.length} passages</span>}</h3>
        {chunks.length > 0 && <label className="v2-search meeting-transcript-search"><span className="sr-only">Find in transcript</span><input value={query} onChange={e => setQuery(e.target.value)} placeholder="Find in transcript…"/></label>}
      </div>
      {!chunks.length && <p className="v2-muted">{job.status === 'no_speech' ? 'No speech was captured in this call.' : 'The transcript will appear after the call and transcription finish.'}</p>}
      {query && <p className="v2-muted" role="status">{shown.length} of {chunks.length} passages match.</p>}
      <div className="meeting-transcript">{shown.map(chunk => <p key={chunk.index} id={`passage-${chunk.index}`} className={focus === chunk.index ? 'is-focused' : undefined}>{chunk.content}</p>)}</div>
    </div>
  </section>;
}
